import { useState } from "react";
import { api, SITE_HOST } from "../api";
import { StatsTab } from "./StatsTab";
import { WebappsTab } from "./WebappsTab";
import { CustomersTab } from "./CustomersTab";
import { AdminsTab } from "./AdminsTab";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";

interface Props {
  meEmail: string;
  meId: string;
  onLogout: () => void;
}

type Tab = "stats" | "webapps" | "customers" | "admins";

const TABS: { key: Tab; label: string }[] = [
  { key: "stats", label: "Stats" },
  { key: "webapps", label: "Webapps" },
  { key: "customers", label: "Customers" },
  { key: "admins", label: "Admins" },
];

function tabFromHash(): Tab {
  const h = typeof window !== "undefined" ? window.location.hash.replace("#", "") : "";
  return TABS.some((t) => t.key === h) ? (h as Tab) : "stats";
}

export function Dashboard({ meEmail, meId, onLogout }: Props) {
  // Remember the open tab in the URL hash so a reload (or a phone coming back
  // from the background) lands on the same list.
  const [tab, setTabState] = useState<Tab>(tabFromHash);
  function setTab(t: Tab) {
    setTabState(t);
    try {
      window.history.replaceState(null, "", `#${t}`);
    } catch {
      // ignore
    }
  }

  async function logout() {
    try {
      await api.logout();
    } catch {
      // ignore
    }
    onLogout();
  }

  return (
    <div className="app">
      <div className="topbar">
        <a className="brand" href={`https://${SITE_HOST}`}>
          <Logo size={22} withDot title={SITE_HOST} />
          <h1>
            login<span className="muted" style={{ fontWeight: 400 }}>.mioe.com.tr</span>
            <span className="muted brand-sep"> · admin</span>
          </h1>
        </a>
        <div className="who">
          <span className="muted" title={meEmail}>{meEmail}</span>
          <ThemeToggle inline />
          <button className="secondary sm" onClick={logout}>Sign out</button>
        </div>
      </div>
      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="content">
        {tab === "stats" ? (
          <StatsTab />
        ) : tab === "webapps" ? (
          <WebappsTab />
        ) : tab === "customers" ? (
          <CustomersTab />
        ) : (
          <AdminsTab meId={meId} />
        )}
      </div>
    </div>
  );
}
